import { productLabelZpl, type DeskProductLabelsQueued } from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../../api';
import { fmt } from '../../format';
import { printZpl } from '../../printing';
import {
  LABEL_SIZE_KEYS,
  MAX_LABEL_COPIES,
  codeChoiceText,
  copyCount,
  deskLabelRequest,
  deskSentText,
  labelCode,
  labelSize,
  type LabelCodeChoice,
  type LabelTarget,
} from '../../productLabels';
import { store } from '../../session';
import { Modal, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { LabelSizePicker } from '../../ui/LabelSize';
import { ZplPreview } from '../../ui/ZplPreview';
import { phone, useLabelDesks } from '../DeskPrinter';

export { labelCode } from '../../productLabels';

/** The destination last chosen on this computer: 'here' or a desk's code. */
const TARGET_KEY = 'wms.productLabelTarget';

/** A product to label. */
export interface LabelProduct {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  /** the code to print: the main unit barcode, or the one "Barkodu bas" was pressed for; null: the label carries the SKU */
  readonly barcode: string | null;
  /** one product: its codes to choose from (labelCodeChoices), a carton's with the units one scan counts */
  readonly codes?: readonly LabelCodeChoice[];
  readonly sellerCode: string | null;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  /** false: the goods take no label; what prints is a card for the shelf or pallet */
  readonly unitLabels: boolean;
  /** units in the warehouse, for "stok kadar" */
  readonly physical: number;
}

/**
 * Product labels printed through this computer's thermal printer, or sent to a packing desk's
 * printer computer (warehouse staff, where one is switched on), without a receipt: for goods that
 * came without a barcode, a barcode just added ("Barkodu bas"), relabelling stock, or labels made
 * ready before goods arrive. One product or many at once, each with its own count; the size is
 * the roll in the printer that prints them.
 */
export function ProductLabelDialog(props: {
  products: readonly LabelProduct[];
  onClose: () => void;
  title?: string;
  /** lot and SKT to start from (one product), e.g. what the dock has typed */
  lotNo?: string;
  expiryDate?: string;
}) {
  const toast = useToast();
  const desks = useLabelDesks();
  // lot, SKT and the choice of code belong to one product's goods
  const single = props.products.length === 1 ? props.products[0]! : null;
  const [chosen, setChosen] = useState<LabelTarget | null>(() => {
    const saved = store.get(TARGET_KEY);
    return saved === 'here' ? 'here' : saved ? { desk: saved } : null;
  });
  // the last destination while it is still offered; a phone has no label printer of its own: a desk first
  const target: LabelTarget =
    chosen !== null && (chosen === 'here' || desks.some((d) => d.code === chosen.desk)) ? chosen : phone() && desks[0] ? { desk: desks[0].code } : 'here';
  const desk = target === 'here' ? null : (desks.find((d) => d.code === target.desk) ?? null);
  const sizeKey = target === 'here' ? LABEL_SIZE_KEYS.here : LABEL_SIZE_KEYS.desk;
  const [sizes, setSizes] = useState(() => ({ here: labelSize(store.get(LABEL_SIZE_KEYS.here), 'here'), desk: labelSize(store.get(LABEL_SIZE_KEYS.desk), { desk: '' }) }));
  const size = target === 'here' ? sizes.here : sizes.desk;
  const [code, setCode] = useState<string | null>(() => (single ? labelCode(single) : null));
  const [copies, setCopies] = useState<Readonly<Record<string, string>>>(() => Object.fromEntries(props.products.map((p) => [p.id, '1'])));
  const [lotNo, setLotNo] = useState(props.lotNo ?? '');
  const [expiry, setExpiry] = useState(props.expiryDate ?? '');
  const [busy, setBusy] = useState(false);

  const choices = single?.codes ?? [];
  const unitsOf = (c: string | null) => choices.find((x) => x.barcode === c)?.unitsPerScan ?? 1;
  const lines = props.products.map((p) => {
    const printed = p === single ? code : labelCode(p);
    return { p, code: printed, count: copyCount(copies[p.id] ?? '') };
  });
  const ready = lines.filter((l): l is { p: LabelProduct; code: string; count: number } => l.code !== null && l.count > 0);
  const total = ready.reduce((n, l) => n + l.count, 0);
  const zplOf = (l: { p: LabelProduct; code: string }, count: number) =>
    productLabelZpl({
      sku: l.p.sku,
      name: l.p.name,
      barcode: l.code,
      size,
      lotNo: single ? lotNo.trim() || null : null,
      expiryDate: single ? expiry || null : null,
      sellerCode: l.p.sellerCode,
      unitsPerScan: l.p === single ? unitsOf(l.code) : 1,
      copies: count,
    });
  const first = ready[0] ?? null;
  const preview = first ? zplOf(first, 1) : null;

  const fill = (pick: (p: LabelProduct) => number) =>
    setCopies(Object.fromEntries(props.products.map((p) => [p.id, String(Math.min(MAX_LABEL_COPIES, Math.max(0, pick(p))))])));

  const print = async () => {
    setBusy(true);
    try {
      store.set(sizeKey, size);
      store.set(TARGET_KEY, target === 'here' ? 'here' : target.desk);
      if (target === 'here') {
        const { pages } = await printZpl(ready.map((l) => zplOf(l, l.count)).join('\n'), single ? `${single.sku} etiketi` : 'Ürün etiketleri');
        toast(`${pages} etiket yazdırmaya gönderildi`, 'ok');
      } else {
        const body = deskLabelRequest(
          size,
          ready.map((l) => ({ productId: l.p.id, barcode: l.code, copies: l.count })),
          single ? { lotNo, expiryDate: expiry } : {},
        );
        const sent = await api<DeskProductLabelsQueued>('POST', `/api/wms/stations/${encodeURIComponent(target.desk)}/product-labels`, body);
        toast(deskSentText(sent), sent.online ? 'ok' : 'info');
      }
      props.onClose();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={props.title ?? (single ? `Ürün etiketi · ${single.sku}` : `Ürün etiketleri · ${props.products.length} ürün`)}
      onClose={props.onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || total === 0} onClick={() => void print()}>
            <Icon name="printer" /> {target === 'here' ? `${fmt.n(total)} etiket yazdır` : `${fmt.n(total)} etiketi ${target.desk} masasına gönder`}
          </button>
        </>
      }
    >
      <div className="split">
        <div className="stack">
          {desks.length > 0 && (
            <div className="field">
              <label htmlFor="label-target">Nereden basılsın</label>
              <select
                id="label-target"
                className="select"
                value={target === 'here' ? 'here' : target.desk}
                onChange={(e) => setChosen(e.target.value === 'here' ? 'here' : { desk: e.target.value })}
              >
                <option value="here">Bu bilgisayarın yazıcısı</option>
                {desks.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.code} masasının yazıcısı{d.name && d.name !== d.code ? ` · ${d.name}` : ''}
                    {d.online ? '' : ' · çevrimdışı'}
                  </option>
                ))}
              </select>
              {desk && !desk.online && (
                <span className="hint bad">Masanın yazıcı bilgisayarı şu an çevrimdışı: açılırsa bir saat içinde basar, yoksa etiket basılmaz.</span>
              )}
            </div>
          )}
          <div className="field">
            <label htmlFor="label-size">{desk ? `Etiket boyutu (${desk.code} yazıcısındaki rulo)` : 'Etiket boyutu (yazıcıdaki rulo)'}</label>
            <LabelSizePicker
              key={target === 'here' ? 'here' : 'desk'}
              id="label-size"
              value={size}
              rotate={target === 'here'}
              onChange={(next) => setSizes(target === 'here' ? { ...sizes, here: next } : { ...sizes, desk: next })}
            />
          </div>
          {single && choices.length > 1 && (
            <div className="field">
              <label htmlFor="label-code">Basılacak barkod</label>
              <select id="label-code" className="select mono" value={code ?? ''} onChange={(e) => setCode(e.target.value)}>
                {choices.map((c) => (
                  <option key={c.barcode} value={c.barcode}>
                    {codeChoiceText(c, single.sku)}
                  </option>
                ))}
              </select>
              {unitsOf(code) > 1 && <span className="hint">Koli barkodu: okutulunca {fmt.n(unitsOf(code))} adet sayar. Etiketi koliye yapıştırın, tek ürüne değil.</span>}
            </div>
          )}
          {props.products.length > 1 && (
            <div className="row" style={{ gap: 6 }}>
              <span className="muted" style={{ fontSize: 13 }}>Adet:</span>
              <button type="button" className="btn sm" onClick={() => fill(() => 1)}>
                Hepsine 1
              </button>
              <button type="button" className="btn sm" onClick={() => fill((p) => (p.unitLabels ? p.physical : 1))} title="Depodaki adet kadar (etiketsiz ürüne 1 kart)">
                Stok kadar
              </button>
            </div>
          )}
          <div className="stack" style={{ gap: 8 }}>
            {lines.map(({ p, code: printed }) => (
              <div key={p.id} className="row" style={{ gap: 8, flexWrap: 'nowrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    <span className="mono">{printed ?? p.sku}</span>
                    {printed && printed === p.sku && !p.barcode && ' · barkodu yok, stok kodu basılır'}
                    {!printed && ' · basılamaz: barkodu yok, stok kodunda Türkçe harf ya da boşluk var (ürüne iç barkod verin)'}
                    {printed && !p.unitLabels && ' · ürüne yapıştırılmaz: raf / palet kartı'}
                    {p.physical > 0 && ` · stok ${fmt.n(p.physical)}`}
                  </div>
                </div>
                <input
                  className="input"
                  aria-label={`${p.sku} etiket adedi`}
                  title="Kaç etiket basılsın"
                  inputMode="numeric"
                  style={{ width: 76, flex: 'none' }}
                  disabled={!printed}
                  value={printed ? (copies[p.id] ?? '') : ''}
                  onChange={(e) => setCopies({ ...copies, [p.id]: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                />
              </div>
            ))}
          </div>
          {single && (single.trackLot || single.trackExpiry) && (
            <div className="row">
              {single.trackLot && (
                <div className="field">
                  <label htmlFor="label-lot">Lot (isteğe bağlı)</label>
                  <input id="label-lot" className="input mono" value={lotNo} onChange={(e) => setLotNo(e.target.value.toUpperCase())} />
                </div>
              )}
              {single.trackExpiry && (
                <div className="field">
                  <label htmlFor="label-expiry">SKT (isteğe bağlı)</label>
                  <input id="label-expiry" className="input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                </div>
              )}
            </div>
          )}
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            {desk
              ? `Etiketler ${desk.code} masasında yazıcı olarak açılan bilgisayardan basılır; ürün başına en çok ${MAX_LABEL_COPIES}. Rulo boyutu o yazıcıdaki ruloyla aynı olmalı.`
              : `Etiketler bu bilgisayarın yazıcısından basılır; ürün başına en çok ${MAX_LABEL_COPIES}. Rulo boyutu yazıcı sürücüsündeki kâğıt boyutuyla aynı olmalı (docs/yazici-kurulumu.md).`}
          </p>
        </div>
        {preview ? <ZplPreview zpl={preview} /> : <div className="muted">Basılacak etiket yok</div>}
      </div>
    </Modal>
  );
}
